import type { de } from './de';

/** English texts of the module, same keys as German. */
export const en: typeof de = {
  name: 'Appointments',
  description:
    'Customer and project appointments, one-off or recurring, with calendar, map and staffing. People signed up get the appointment in their Outlook calendar.',
  nav: { appointments: 'Appointments', requests: 'Requests', report: 'Report' },
  entities: { appointment: 'Appointment', request: 'Appointment request' },
  permissions: {
    appointment: {
      read: {
        label: 'View appointments',
        description:
          'Open the list, calendar and map of appointments. With scope “Own” only appointments the person is signed up for or created.',
      },
      join: {
        label: 'Sign up for appointments',
        description: 'Sign oneself up for appointments and off again.',
      },
      write: {
        label: 'Plan appointments',
        description:
          'Create and change appointments and series, assign people and cancel appointments. With scope “Own” only own appointments.',
      },
      delete: { label: 'Delete appointments', description: 'Delete appointments and series.' },
      comment: {
        label: 'Comment on appointments',
        description: 'Write comments on appointments.',
      },
      report: {
        label: 'Appointment report',
        description:
          'Evaluate the hours of the appointments in a period and export them as a CSV file, e.g. for invoicing.',
      },
    },
    request: {
      manage: {
        label: 'Handle appointment requests',
        description:
          'See requests from e-mails to the address of the company, take them over as appointment or decline them. Taking over also needs “Plan appointments”.',
      },
    },
    settings: {
      manage: {
        label: 'Set up appointments',
        description: 'Maintain the appointment types and functions of the company.',
      },
    },
  },
  roles: {
    participant: 'Appointments: View and sign up',
    planner: 'Appointments: Plan',
    manager: 'Appointments: Manage',
  },
  sections: { appointments: 'Appointments' },
  dashboard: { mine: 'My appointments' },
  catalogs: {
    types: {
      label: 'Appointment types',
      description: 'Kinds of appointments, e.g. inspection, customer meeting or acceptance.',
    },
    functions: {
      label: 'Functions',
      description: 'Who an appointment needs, e.g. fire safety officer or SiGeKo.',
    },
  },
  notifications: {
    assigned: 'You were assigned to an appointment',
    request: 'New appointment request by e-mail',
  },
  ai: { readMail: 'Read appointment from e-mail' },
  audit: {
    appointment: {
      create: 'Appointment created',
      update: 'Appointment changed',
      delete: 'Appointment deleted',
      join: 'Signed up for appointment',
      leave: 'Signed off from appointment',
      cancel: 'Appointment cancelled',
      restore: 'Appointment restored',
    },
    series: { extend: 'Series continued' },
    request: {
      create: 'Appointment request received',
      accept: 'Appointment request taken over',
      decline: 'Appointment request declined',
    },
  },
  guides: {
    appointments: {
      title: 'Plan and staff appointments',
      summary: 'Create appointments and series, sign up, Outlook and who may do what.',
    },
  },
  errors: {
    title: 'Please enter a title (at most 200 characters).',
    date: 'Please enter a valid date.',
    time: 'Please enter start and end as a time.',
    endBeforeStart: 'The end must be after the start.',
    tooLong: 'An appointment lasts at most 31 days.',
    range: 'The period is invalid or longer than 400 days.',
    type: 'Please choose an active appointment type.',
    function: 'Please choose an active function.',
    project: 'Please choose a project of the company that you may see and that is not archived.',
    projectArchived: 'The project is archived.',
    object: 'Please choose an object that is available in projects and not archived.',
    objectOfProject: 'The object does not belong to this project.',
    place: 'The place is incomplete. Please search or enter the address again.',
    placeEmpty: 'Please enter a name, street, town or a point on the map.',
    placeObject: 'For “At the object” please choose an object first.',
    placeLabel: 'The name of the place may have at most 200 characters.',
    street: 'Street and house number are too long.',
    postcode: 'The postcode is too long.',
    city: 'The town is too long.',
    country: 'Please enter a valid country code, e.g. DE.',
    meetingUrl: 'Please enter a link that starts with https://.',
    description: 'The description may have at most 5,000 characters.',
    staff: 'Please choose active accounts of the company.',
    tooManyStaff: 'At most 20 people per appointment.',
    guest: 'The person without an account is not valid.',
    guestName: 'Please enter a name (at most 100 characters).',
    guestEmail: 'Please enter a valid e-mail address or leave the field empty.',
    staffNeeded: 'Please enter a number from 0 to 20.',
    repeat: 'Please choose a repetition.',
    repeatUntil: 'Please enter a valid end date.',
    repeatCount: 'Please enter a number from 2 to 500.',
    repeatEnd: 'Please enter either an end date or a number.',
    repeatScope: 'The repetition can only change for this and all following appointments.',
    slotDay: 'Please choose a day within the window of the appointment.',
    slotPast: 'The day is in the past.',
  },
  pages: {
    allDay: 'all day',
    days: '{{from}} to {{to}}',
    today: 'Today',
    nobody: 'nobody yet',
    online: 'Online',
    series: 'Series',
    dayOpen: 'Day open',
    dayOpenWindow: 'Day open, {{from}} to {{to}}',
    slot: {
      person: 'Who goes',
      assign: 'Assign to someone else',
      kind: 'Person',
      account: 'With account',
      guest: 'Without account',
      guestName: 'Name',
      guestNamePlaceholder: 'e.g. Max Muster, Muster Bau GmbH',
      guestEmail: 'E-mail',
      guestEmailHint: 'With an address the invitation comes by e-mail, without one none.',
      hintGuest: 'The person is signed up by name.',
      moreLabel: 'More options for “{{title}}”',
      hintOther: 'The person is signed up and gets a notice and the invitation by e-mail.',
      title: 'Take appointment',
      changeTitle: 'Change day',
      description: '“{{title}}” takes place once between {{from}} and {{to}}. Choose day and time.',
      day: 'Day',
      hint: 'You are signed up and get the invitation by e-mail.',
      noDays: 'No day of the window is left.',
      submit: 'Take',
      saved: '“{{title}}” is set for {{day}}.',
      take: 'Take',
      takeLabel: 'Take “{{title}}” and choose the day',
      choose: 'Choose day',
      chooseLabel: 'Choose the day for “{{title}}”',
      change: 'Change day',
      clear: 'Leave day open',
      cleared: 'The day is open again.',
      window: 'Window',
      windowText: '{{from}} to {{to}}',
    },
    fields: { place: 'Place', project: 'Project' },
    staffing: {
      none: 'Unstaffed, {{needed}} needed',
      partly: '{{taken}} of {{needed}} staffed',
      full: 'Staffed',
      cancelled: 'Cancelled',
    },
    legend: {
      label: 'Colours',
      none: 'unstaffed',
      partly: 'partly staffed',
      full: 'staffed',
      cancelled: 'cancelled',
    },
    join: {
      join: 'Count me in',
      leave: 'Sign off',
      joinLabel: 'Sign up for “{{title}}”',
      leaveLabel: 'Sign off from “{{title}}”',
      joined: 'You are signed up for “{{title}}”. The invitation comes by e-mail.',
      left: 'You are signed off from “{{title}}”.',
    },
    rule: {
      daily_one: 'Daily',
      daily_other: 'Every {{count}} days',
      weekly_one: 'Every week',
      weekly_other: 'Every {{count}} weeks',
      monthly_one: 'Every month',
      monthly_other: 'Every {{count}} months',
      yearly_one: 'Every year',
      yearly_other: 'Every {{count}} years',
      on: 'on {{days}}',
      until: 'until {{day}}',
      count_one: '{{count}} appointment',
      count_other: '{{count}} appointments',
    },
    list: {
      title: 'Appointments',
      description:
        'Appointments of this and the next two weeks or of another period. Drag Outlook appointments or e-mails here to create an appointment from them.',
      descriptionReadOnly: 'Appointments of this and the next two weeks or of another period.',
      new: 'New appointment',
      previous: 'Earlier period',
      next: 'Later period',
      today: 'Today',
      period: '{{from}} to {{to}}',
      span: 'Period',
      spans: {
        s1: '1 week',
        s2: '2 weeks',
        s3: '3 weeks',
        s4: '4 weeks',
        smonth: 'Month',
        scustom: 'Custom period',
      },
      periodFrom: 'From',
      periodTo: 'To',
      periodInvalid: 'Please choose an end after the start, at most one year later.',
      nobodyYet: 'nobody yet',
      views: 'View',
      viewList: 'List',
      viewCalendar: 'Calendar',
      viewMap: 'Map',
      who: 'Which appointments',
      whoAll: 'All',
      whoMine: 'Mine',
      whoOpen: 'Unstaffed',
      search: 'Search title, place, project or person',
      searchPlaceholder: 'Title, place, person …',
      type: 'Appointment type',
      allTypes: 'All appointment types',
      function: 'Function',
      allFunctions: 'All functions',
      count_one: '{{count}} appointment',
      count_other: '{{count}} appointments',
      unstaffed_one: '{{count}} still needs people',
      unstaffed_other: '{{count}} still need people',
      allStaffed: 'All staffed',
      empty: 'No appointments in this period',
      emptyHint: 'Create an appointment or drag an Outlook appointment here.',
      emptyReadOnly: 'Planned appointments appear here.',
      noMatches: 'No matching appointments',
      noneOpen: 'Everything is staffed',
      noneOpenHint: 'Every appointment in this period has enough people.',
      calendarLabel: 'Calendar of appointments, {{period}}',
      more: '+{{count}} more',
      dayEmpty: 'No appointments on {{day}}',
      mapLabel: 'Map of appointments',
      mapEmpty: 'None of these appointments has a place with coordinates.',
      mapMissing_one: '{{count}} appointment without a place is only in the list.',
      mapMissing_other: '{{count}} appointments without a place are only in the list.',
      drop: {
        button: 'Take over from Outlook',
        overlay: 'Drop to create an appointment from it',
        notice: 'Taken over from “{{name}}”. Please check and complete.',
        unreadable:
          'No appointment can be read from this file. Outlook appointments and e-mails (.msg, .eml) and calendar files (.ics) work.',
      },
    },
    form: {
      newTitle: 'New appointment',
      newDescription: 'One-off or recurring. Assigned people get an invitation by e-mail.',
      editTitle: 'Edit appointment',
      editDescription: 'People signed up get the change in their calendar.',
      create: 'Create',
      created: 'Appointment created.',
      saved: 'Appointment saved.',
      scope: 'What should change?',
      scopeOne: 'Only this appointment',
      scopeFollowing: 'This and all following appointments',
      scopeFollowingHint: 'Appointments changed on their own stay as they are.',
      title: 'Title',
      titlePlaceholder: 'e.g. Inspection day care centre',
      type: 'Appointment type',
      function: 'Needed',
      functionHint: 'Function that attends the appointment, e.g. SiGeKo.',
      when: 'When',
      timing: 'Kind of appointment',
      timingTime: 'With time',
      timingAllDay: 'All day',
      timingOpen: 'Day open',
      windowFrom: 'Earliest on',
      windowTo: 'Latest on',
      windowHint:
        'The appointment takes place once in this period. Whoever takes it chooses day and time. With “Weekly” the window repeats every week.',
      date: 'Date',
      endDay: 'Up to and including',
      from: 'From',
      to: 'To',
      repeat: 'Repeat',
      repeatNever: 'Do not repeat',
      presets: {
        daily: 'Daily',
        workdays: 'Every weekday (Mon–Fri)',
        weekly: 'Every week on {{day}}',
        biweekly: 'Every 2 weeks on {{day}}',
        monthly: 'Every month {{on}}',
        quarterly: 'Every 3 months {{on}}',
        yearly: 'Every year on {{date}}',
        custom: 'Custom rule …',
      },
      every: 'Repeat every',
      unit: 'Unit',
      units: {
        daily_one: 'day',
        daily_other: 'days',
        weekly_one: 'week',
        weekly_other: 'weeks',
        monthly_one: 'month',
        monthly_other: 'months',
        yearly_one: 'year',
        yearly_other: 'years',
      },
      weekdays: 'On these days',
      weekdaysHint: 'Without a choice on the weekday of the first appointment ({{day}}).',
      monthly: 'In the month',
      monthlyOn: {
        day: 'on day {{date}}',
        weekday: 'on the {{nth}} {{day}}',
        lastWeekday: 'on the last {{day}}',
        lastDay: 'on the last day of the month',
      },
      nth: { n1: 'first', n2: 'second', n3: 'third', n4: 'fourth' },
      repeatEnd: 'Ends',
      repeatEndNever: 'Never',
      repeatEndUntil: 'On a date',
      repeatEndCount: 'After a number',
      until: 'Last possible day',
      count: 'Number of appointments',
      nextDates: 'Next dates: {{dates}}',
      where: 'Where',
      project: 'Project',
      object: 'Object',
      place: 'Place',
      placeKinds: { object: 'At the object', address: 'Own address', none: 'No place' },
      placeObjectHint: 'The appointment takes place at the address of the object.',
      placeObjectMissing: 'Please choose an object above.',
      addressSearch: 'Search address',
      addressSearchHint: 'Fills the fields below and shows the appointment on the map.',
      placeLabel: 'Name',
      placeLabelPlaceholder: 'e.g. Town hall, meeting room 2',
      street: 'Street',
      houseNumber: 'No.',
      postcode: 'Postcode',
      city: 'Town',
      online: 'Online appointment',
      onlineHint: 'Teams, Zoom or another online meeting.',
      meetingUrl: 'Meeting link',
      meetingUrlPlaceholder: 'https://teams.microsoft.com/…',
      meetingUrlHint: 'Shown in the invitation and on the appointment page.',
      who: 'Who',
      staffNeeded: 'Needed',
      staffNeededHint: 'People',
      staff: 'Assigned',
      staffHint: 'Others sign up themselves.',
      staffSeriesHint: 'These people take part in every appointment of the series.',
      guests: 'Without account',
      guestsHint:
        'For people who do not use the app. With an e-mail address they get the invitation by e-mail, without one none.',
      guestsAdd: 'Person without account',
      guestRow: 'Person without account {{number}}',
      guestRemove: 'Remove person without account {{number}}',
      guestName: 'Name',
      guestEmail: 'E-mail (optional)',
      description: 'Description',
      descriptionPlaceholder: 'Agenda, contact on site, access …',
    },
    detail: {
      notFound: 'Appointment not found',
      notFoundHint: 'It was deleted, or you may not see it.',
      when: 'When',
      series: 'Series',
      download: 'Add to calendar',
      cancel: 'Cancel appointment',
      restore: 'Restore',
      cancelled: 'Appointment cancelled. People signed up get the cancellation.',
      restored: 'Appointment restored.',
      cancelledTitle: 'This appointment is cancelled',
      cancelledHint: 'It stays visible; nobody can sign up any more.',
      cancelTitle: 'Cancel appointment?',
      cancelDescription:
        '“{{title}}” is cancelled. Everyone signed up gets the cancellation in their calendar.',
      deleteTitle: 'Delete appointment?',
      deleteDescription:
        '“{{title}}” is deleted with its comments. People signed up get a cancellation.',
      deleteScope: 'Which appointments to delete',
      deleteOne: 'Only this appointment',
      deleteFollowing: 'This and all following of the series',
      deleted: 'Appointment deleted.',
      whereTitle: 'When and where',
      place: 'Place',
      noPlace: 'No place given',
      online: 'Online',
      joinMeeting: 'Join the meeting',
      onlineNoLink: 'Link to follow',
      project: 'Project',
      object: 'Object',
      staffTitle: 'Staffing',
      staffing: 'Status',
      function: 'Needed',
      staff: 'Signed up',
      guest: 'without account',
      invites: 'Outlook',
      invitesHint:
        'People signed up get an invitation by e-mail, at most four weeks before the appointment.',
      description: 'Description',
      map: 'Map',
      activity: 'Comments and history',
      changed: 'Last changed {{when}}',
    },
    report: {
      title: 'Report',
      description:
        'Hours of the appointments in a period, e.g. for invoicing: each appointment with its duration and the total. Cancelled appointments do not count.',
      caption: 'Appointments {{period}}',
      previous: 'Earlier period',
      next: 'Later period',
      thisMonth: 'This month',
      span: 'Period',
      spans: { month: 'Month', custom: 'Custom period' },
      project: 'Project',
      allProjects: 'All projects',
      searchProject: 'Search project: number or name',
      noProject: 'No project found',
      totalRow: 'Total',
      withoutProject: 'No project',
      withoutProjectHint_one: 'One appointment has no project. Please assign it for invoicing.',
      withoutProjectHint_other:
        '{{count}} appointments have no project. Please assign them for invoicing.',
      showWithoutProject: 'Show',
      assign: {
        button: 'Assign',
        label: 'Assign project to “{{title}}”',
        title: 'Assign project',
        project: 'Project',
        projectMissing: 'Please choose a project.',
        object: 'Object',
        following: 'Also all following appointments of the series',
        followingHint: 'Appointments changed on their own stay as they are.',
        saved: '“{{title}}” is assigned.',
      },
      totalHours: 'Total hours',
      hoursUnit: 'h',
      count: 'Appointments',
      withoutHours: 'Without time',
      withoutHoursHint:
        'All-day appointments and appointments whose day is still open have no time and do not count towards the hours.',
      noHours: 'no time',
      columns: {
        day: 'Date',
        time: 'Time',
        title: 'Appointment',
        project: 'Project',
        staff: 'Taken by',
        hours: 'Hours',
      },
      empty: 'No appointments in this period',
      emptyHint: 'Choose another month or a custom period.',
      fileName: 'Appointment report {{period}}',
    },
    section: {
      new: 'New appointment',
      empty: 'No upcoming appointments',
      emptyHint: 'Create the first appointment.',
      emptyReadOnly: 'Planned appointments appear here.',
      more_one: 'One more appointment is in the list of appointments.',
      more_other: '{{count}} more appointments are in the list of appointments.',
    },
    tiles: {
      mineEmpty: 'No appointments in the next two weeks',
      mineEmptyHint: 'Sign up for an appointment under Appointments.',
    },
    requests: {
      title: 'Appointment requests',
      description:
        'Appointments that came by e-mail to the company address and still need a check.',
      addressTitle: 'Address for appointments',
      addressHint:
        'Forward invitations and mails about appointments to this address. If the mail comes from your account, the app creates the appointment right away. All other mails land here.',
      address: 'E-mail address',
      copy: 'Copy address',
      copied: 'Address copied',
      status: 'Show',
      open: 'Open',
      done: 'Done',
      from: 'From {{sender}}',
      fromAccount: 'Company account',
      received: 'Received {{when}}',
      noSubject: '(no subject)',
      proposal: 'Proposal',
      noProposal: 'No time found. Enter it when you accept, or let AI read the mail.',
      allDay: 'all day',
      text: 'Text of the mail',
      accept: 'Accept',
      read: 'Read with AI',
      readDone: 'Proposal updated',
      decline: 'Decline',
      declineTitle: 'Decline appointment request?',
      declineDescription:
        'The request “{{subject}}” is filed as declined. The sender gets no message.',
      declined: 'Appointment request declined',
      acceptNotice: 'From the mail of {{sender}}. Check the details before you save.',
      accepted: 'Accepted',
      declinedBadge: 'Declined',
      openAppointment: 'Open appointment',
      empty: 'No open appointment requests',
      emptyHint: 'Mails to the address above appear here.',
      emptyDone: 'Nothing done yet',
      emptyDoneHint: 'Accepted and declined requests stay here for 30 days.',
    },
    errors: {
      invalidInput: 'Please check the marked fields.',
      versionConflict:
        'The appointment was changed in the meantime. Please reload the page and try again.',
      cancelled: 'The appointment is cancelled.',
      past: 'The appointment is over.',
      full: '20 people are already signed up for this appointment.',
      notFound: 'The appointment no longer exists.',
      forbidden: 'You lack the permission for this.',
      slotTaken: 'Someone has already taken this appointment.',
      dayFixed: 'This appointment has a fixed day.',
      requestDecided: 'Someone has already decided on this request.',
      ai: 'AI could not read the mail right now. Please try again later.',
    },
  },
};
